import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import ProfessionalPhoto from "../../assets/ProfessionalPhoto2.png";
import "../../styles/ProfessionalMode.css";

// Animation variant for scroll-reveal sections
const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

// Reusable scroll-reveal section component
const RevealSection: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.section
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={sectionVariants}
    >
      {children}
    </motion.section>
  );
};

const ProfessionalMode: React.FC = () => {
  const experience = [
    {
      period: "August 2020 - Present",
      title: "Web Developer",
      company: "EasternGraphics GmbH",
      location: "Ilmenau, Germany",
      type: "permanent",
      description:
        "Frontend development with React, TypeScript, PHP, and WordPress. Documentation through internal wiki articles. Connecting and integrating multiple complex projects. Close collaboration with designers using Figma, Photoshop, and After Effects. Code architecture brainstorming and solution design. Code review and quality assurance in an agile environment.",
    },
    {
      period: "November 2019 - December 2019",
      title: "Data Analyst",
      company: "NOVASIB GmbH",
      location: "Ilmenau, Germany",
      type: "student",
      description:
        "Analyzed complex datasets to extract meaningful insights. Created data visualizations and reports. Collaborated with cross-functional teams on data-driven projects.",
    },
    {
      period: "2018 - 2019",
      title: "Frontend Developer",
      company: "Thomas Biber Deutschland GmbH",
      location: "Ilmenau, Germany",
      type: "student",
      description:
        "Implemented and customized Angular 2+ components with full test coverage. Programmed using Spring Framework tools. Implemented CSS stylesheets according to designer specifications. Developed automated Karma + Jasmine tests.",
    },
    {
      period: "March 2018",
      title: "Unreal Engine Level Designer",
      company: "TU Ilmenau",
      location: "Ilmenau, Germany",
      type: "student",
      description:
        "Designed and developed immersive VR environments. Created interactive 3D scenes using Unreal Engine. Collaborated on VR research projects.",
    },
    {
      period: "2016 - 2017",
      title: "Android App Developer",
      company: "KAST Programming",
      location: "Cairo, Egypt",
      type: "freelance",
      description:
        "Developed and launched Wassalny Taxi applications for Android and iOS. Created responsive website using modern web technologies. Integrated Google Maps JavaScript API for location services.",
    },
    {
      period: "2015 - 2017",
      title: "Founder & Lead Developer",
      company: "LecturActive",
      location: "Cairo, Egypt",
      type: "startup",
      description:
        "Co-founded educational technology startup with four partners. Developed front-end for desktop, Android, and iOS applications. Designed and developed company website and promotional materials. Participated in VentureLab's Boot camp program.",
    },
  ];

  const education = [
    {
      degree: "M.Sc. Medientechnologie",
      institution: "Technische Universität Ilmenau",
      location: "Ilmenau, Germany",
      year: "2017 - 2020",
      thesis: "Presence: Virtual Hands & Rigid Bodies",
      details:
        "Specialized in Web Application Development, Multivariate Signal Processing, and Virtual & Augmented Reality.",
    },
    {
      degree: "B.Sc. Digital Media Engineering",
      institution: "German University in Cairo",
      location: "Cairo, Egypt",
      year: "2010 - 2015",
      thesis: "Challenges in VoIP Systems (Grade: A+)",
      details: "Computer Engineering sub-discipline with overall grade B.",
    },
  ];

  const skills = [
    "JavaScript",
    "TypeScript",
    "React",
    "Angular",
    "Python",
    "Java",
    "HTML/CSS",
    "Git",
    "Unreal Engine",
    "VR/AR",
    "Photoshop",
    "After Effects",
  ];

  const languages = [
    { language: "English", level: "Native" },
    { language: "Arabic", level: "Native" },
    { language: "German", level: "B1" },
    { language: "French", level: "A1" },
  ];

  const awards = [
    {
      title: "MUM2020 Best Paper Award",
      context: "ACM Digital Library",
      link: "https://dl.acm.org/doi/10.1145/3428361.3428388",
    },
    {
      title: "Runner Up - 4th Student Game Developer Conference",
      context: "VR Game 'Hiraeth' - TU Ilmenau, 2018",
    },
    {
      title: "Best 3D Maya Simulation",
      context: "Computer Graphics Project - GUC, 2013",
    },
    {
      title: "Video Editing Award",
      context: "The GUC Insider, 2012",
    },
  ];

  return (
    <div className="professional-mode">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Link to="/" className="back-button">
          <span className="back-arrow">←</span>
          <span>Back</span>
        </Link>
      </motion.div>

      {/* Download CV Button */}
      <motion.a
        href="/cv.pdf"
        target="_blank"
        className="download-cv"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        Download CV
      </motion.a>

      <div className="cv-container">
        {/* Header Section */}
        <motion.header
          className="cv-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="cv-photo">
            <img src={ProfessionalPhoto} alt="Mostafa Elbehery" />
          </div>
          <h1 className="cv-name">Mostafa Elbehery</h1>
          <p className="cv-title">Computer Engineer & Web Developer</p>
          <p className="cv-subtitle">
            Master's in Medientechnologie | 5+ Years Experience
          </p>
          <div className="cv-contact">
            <span>mbehery@protonmail.com</span>
            <span className="separator">·</span>
            <span>Germany</span>
          </div>
        </motion.header>

        {/* Languages Section - Moved up */}
        <RevealSection className="cv-section cv-languages-top">
          <h2 className="section-title">Languages</h2>
          <div className="languages-list">
            {languages.map((lang, index) => (
              <div key={index} className="language-item">
                <span className="language-name">{lang.language}</span>
                <span className="language-level">{lang.level}</span>
              </div>
            ))}
          </div>
        </RevealSection>

        {/* Experience Section */}
        <RevealSection className="cv-section">
          <h2 className="section-title">Experience</h2>
          <div className="experience-list">
            {experience.map((exp, index) => (
              <div key={index} className="experience-item">
                <div className="exp-period">{exp.period}</div>
                <div className="exp-content">
                  <div className="exp-title-row">
                    <h3 className="exp-title">{exp.title}</h3>
                    {exp.type === "student" && (
                      <span className="exp-type-chip student">Student Job</span>
                    )}
                  </div>
                  <p className="exp-company">{exp.company}</p>
                  <p className="exp-location">{exp.location}</p>
                  <p className="exp-description">{exp.description}</p>
                </div>
              </div>
            ))}
          </div>
        </RevealSection>

        {/* Education Section */}
        <RevealSection className="cv-section">
          <h2 className="section-title">Education</h2>
          <div className="education-list">
            {education.map((edu, index) => (
              <div key={index} className="education-item">
                <div className="edu-year">{edu.year}</div>
                <div className="edu-content">
                  <h3 className="edu-degree">{edu.degree}</h3>
                  <p className="edu-institution">{edu.institution}</p>
                  <p className="edu-location">{edu.location}</p>
                  <p className="edu-details">{edu.details}</p>
                  <p className="edu-thesis">Thesis: {edu.thesis}</p>
                </div>
              </div>
            ))}
          </div>
        </RevealSection>

        {/* Skills Section */}
        <RevealSection className="cv-section">
          <h2 className="section-title">Skills</h2>
          <div className="skills-tags">
            {skills.map((skill, index) => (
              <span key={index} className="skill-tag">
                {skill}
              </span>
            ))}
          </div>
        </RevealSection>

        {/* Awards Section */}
        <RevealSection className="cv-section">
          <h2 className="section-title">Awards</h2>
          <div className="awards-list">
            {awards.map((award, index) => (
              <div key={index} className="award-item">
                <h3 className="award-title">
                  {award.link ? (
                    <a
                      href={award.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {award.title}
                    </a>
                  ) : (
                    award.title
                  )}
                </h3>
                <p className="award-context">{award.context}</p>
              </div>
            ))}
          </div>
        </RevealSection>

        {/* Links Section */}
        <RevealSection className="cv-section cv-links">
          <div className="social-links">
            <a
              href="https://www.linkedin.com/in/mostafa-el-beheiry/"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
            <a
              href="https://www.xing.com/profile/Mostafa_Elbehery/cv"
              target="_blank"
              rel="noopener noreferrer"
            >
              Xing
            </a>
          </div>
        </RevealSection>
      </div>
    </div>
  );
};

export default ProfessionalMode;
